import { campaigns, delivery } from '@bb/contracts';
import { sameContent } from '../../editor/normalize.ts';
import type { CampaignContent, CampaignDraftIssue, CampaignView, CampaignWizardStep, UpdateCampaignDraftInput } from '../../../api/client.ts';
import type { MessagesDraft } from '../compose/messagesDraft.ts';

export type WizardStepId = CampaignWizardStep;
type Part = keyof CampaignContent;

/** A problem shown in the wizard: the step that fixes it, how bad it is and what to do. */
export interface WizardIssue {
  step: WizardStepId;
  severity: 'error' | 'warning';
  message: string;
  /** Dotted content path (`messages.variants.0…`); `''` for the step as a whole. */
  path: string;
  code?: string;
}

/** The wizard's working copy: the name, the goal and the content parts as edited (messages may be mid-edit). */
export interface CampaignWizardDraft {
  name: string;
  description: string;
  content: Omit<CampaignContent, 'messages'> & { messages: [] | MessagesDraft };
}

/**
 * A step of the campaign wizard (BB-97): its label, the content parts it edits and the client checks of those parts
 * (the server's contract, so an edit that would be refused is never sent). `lockedAfterLaunch` parts are read-only
 * once the campaign has been launched; the server refuses them too.
 */
export interface WizardStep {
  id: WizardStepId;
  label: string;
  parts: readonly Part[];
  validate: (draft: CampaignWizardDraft) => WizardIssue[];
  lockedAfterLaunch?: readonly Part[];
}

const sentence = (text: string) => {
  const t = text.trim();
  const capital = t.charAt(0).toUpperCase() + t.slice(1);
  return /[.!?]$/.test(capital) ? capital : `${capital}.`;
};

/** Issues of the contract for one content part as it is now. */
export function partIssues(draft: CampaignWizardDraft, part: Part, step: WizardStepId): WizardIssue[] {
  const result = campaigns.CampaignContentPatch.shape[part].safeParse(draft.content[part]);
  if (result.success) return [];
  return result.error.issues.map((i) => ({ step, severity: 'error' as const, path: [part, ...i.path.map(String)].join('.'), message: sentence(i.message) }));
}

const issuesOfParts = (step: WizardStepId, parts: readonly Part[]) => (draft: CampaignWizardDraft) => parts.flatMap((part) => partIssues(draft, part, step));

export const WIZARD_STEPS: readonly WizardStep[] = [
  {
    id: 'compose',
    label: 'Compose',
    parts: ['messages'],
    validate: (draft) => [...(draft.name.trim() ? [] : [{ step: 'compose' as const, severity: 'error' as const, path: 'name', message: 'Name the campaign.' }]), ...issuesOfParts('compose', ['messages'])(draft)],
  },
  { id: 'schedule', label: 'Schedule', parts: ['schedule', 'deliveryControls', 'exemptions'], validate: issuesOfParts('schedule', ['schedule', 'deliveryControls', 'exemptions']) },
  { id: 'target', label: 'Target', parts: ['audience'], validate: issuesOfParts('target', ['audience']) },
  { id: 'conversions', label: 'Assign conversions', parts: ['conversions'], validate: issuesOfParts('conversions', ['conversions']), lockedAfterLaunch: ['conversions'] },
  { id: 'review', label: 'Review', parts: [], validate: () => [] },
];

export const stepLabel = (id: WizardStepId) => WIZARD_STEPS.find((s) => s.id === id)!.label;

const PARTS: readonly Part[] = ['messages', 'schedule', 'audience', 'conversions', 'deliveryControls', 'exemptions'];

/** The wizard's copy of what the campaign works on: its draft, else the running version. */
export function toWizardDraft(campaign: CampaignView): CampaignWizardDraft {
  const content = campaign.draft?.content ?? campaign.published?.content ?? { messages: [], schedule: {}, audience: null, conversions: [], deliveryControls: {}, exemptions: {} };
  return { name: campaign.name, description: campaign.description ?? '', content: structuredClone(content) as CampaignWizardDraft['content'] };
}

/** Parts that pass the contract now: what the server may check (or store) in place of the saved ones. */
export function validParts(draft: CampaignWizardDraft): Partial<CampaignContent> {
  return Object.fromEntries(PARTS.filter((part) => campaigns.CampaignContentPatch.shape[part].safeParse(draft.content[part]).success).map((part) => [part, draft.content[part]])) as Partial<CampaignContent>;
}

/** The campaign's reporting channels for its messages (list filters, reports); `null` until messages are composed. */
export function channelsOf(draft: CampaignWizardDraft): CampaignView['channels'] | null {
  const messages = draft.content.messages;
  return Array.isArray(messages) || messages.channels.length === 0 ? null : delivery.reportingChannelsOf(messages.channels);
}

/** The edit that turns `saved` into `draft` (`null` when nothing changed); the declared channels follow the messages. */
export function draftPatch(draft: CampaignWizardDraft, saved: CampaignWizardDraft, campaign: CampaignView): Omit<UpdateCampaignDraftInput, 'draftVersion'> | null {
  const content = Object.fromEntries(PARTS.filter((part) => !sameContent(draft.content[part], saved.content[part])).map((part) => [part, draft.content[part]])) as Partial<CampaignContent>;
  const channels = channelsOf(draft);
  const body: Omit<UpdateCampaignDraftInput, 'draftVersion'> = {
    ...(draft.name.trim() !== saved.name ? { name: draft.name.trim() } : {}),
    ...(draft.description.trim() !== saved.description ? { description: draft.description.trim() } : {}),
    ...(channels && !sameContent([...channels].sort(), [...campaign.channels].sort()) ? { channels } : {}),
    ...(Object.keys(content).length ? { content } : {}),
  };
  return Object.keys(body).length ? body : null;
}

/** The server's issue in the wizard's terms. */
export const fromServer = (issue: CampaignDraftIssue): WizardIssue => ({ step: issue.step, severity: issue.severity, message: issue.message, path: issue.path, code: issue.code });

/** The content part an issue path is about (`messages.variants.0` → `messages`). */
export const partOf = (path: string): string => path.split('.')[0] ?? '';
